import React from 'react';
import styles from './OfferDetailPage.module.css';

export interface WorkflowStep {
  id: string;
  actionLabel: string;
  completedLabel: string;
  completed: boolean;
  onClick?: () => void;
  onCompletedClick?: () => void;
  disabled?: boolean;
  show?: boolean;
  icon: React.ReactNode;
  completedIcon?: React.ReactNode;
  completedAt?: string;
  partialProgress?: string;
  partialProgressDate?: string;
}

interface WorkflowTimelineProps {
  steps: WorkflowStep[];
}

export function WorkflowTimeline({ steps }: WorkflowTimelineProps): React.JSX.Element {
  const visibleSteps = steps.filter((step) => step.show !== false);

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
  };

  return (
    <div className={styles.workflowContainer}>
      <div className={styles.workflowSteps}>
        {visibleSteps.map((step, index) => {
          const isDone = step.completed;
          const hasPartialProgress = !!step.partialProgress;
          const prevStepDone = index > 0 ? visibleSteps[index - 1].completed : true;
          const isNextAction = !isDone && !step.disabled;
          const isClickable = isDone ? !!step.onCompletedClick : (!!step.onClick && !step.disabled);
          const shouldDim = !isDone && step.disabled && !hasPartialProgress;
          const isInProgress = hasPartialProgress && !isDone;

          return (
            <div
              key={step.id}
              className={styles.workflowStep}
              style={{
                cursor: isClickable ? 'pointer' : 'default',
                opacity: shouldDim ? 0.4 : 1,
              }}
              onClick={() => {
                if (isDone && step.onCompletedClick) {
                  step.onCompletedClick();
                } else if (!isDone && step.onClick && !step.disabled) {
                  step.onClick();
                }
              }}
            >
              {index > 0 && (
                <div
                  className={styles.workflowConnector}
                  style={{
                    background: prevStepDone || (index > 0 && visibleSteps[index - 1].partialProgress) ? '#021a4a' : '#e0e3ef',
                  }}
                />
              )}

              <div
                className={styles.workflowCircle}
                style={{
                  background: isDone ? '#021a4a' : isInProgress ? '#7c3aed' : '#fff',
                  border: isDone || isInProgress ? 'none' : `2px solid ${isNextAction ? '#021a4a' : '#ccc'}`,
                  boxShadow: isNextAction || isInProgress ? '0 0 0 4px rgba(2, 26, 74, 0.1)' : 'none',
                }}
              >
                {step.icon}
              </div>

              <div
                className={styles.workflowLabel}
                style={{
                  color: isDone ? '#0f2b5a' : isInProgress ? '#7c3aed' : isNextAction ? '#021a4a' : '#999',
                }}
              >
                {isDone ? step.completedLabel : step.actionLabel}
              </div>

              {isDone && step.completedAt && (
                <div className={styles.workflowDate} style={{ color: '#021a4a' }}>
                  {formatDate(step.completedAt)}
                </div>
              )}

              {isInProgress && step.partialProgressDate && (
                <div className={styles.workflowDate} style={{ color: '#7c3aed' }}>
                  {formatDate(step.partialProgressDate)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
